import { Link } from "react-router-dom";
import { ArrowUpRight, Waves } from "lucide-react";
import { SHIPS } from "@/data/ships";
import useReveal from "@/hooks/useReveal";

export default function Timeline() {
  return (
    <section
      id="timeline"
      className="relative z-10 pb-16 sm:pb-24 md:pb-32 bg-[var(--c-bg)] scroll-mt-16 sm:scroll-mt-20"
      data-testid="timeline-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
          <header className="max-w-3xl mb-12 sm:mb-20 md:mb-28">
          <p className="overline mb-3 sm:mb-4">Your Career, Chronologically</p>
          <h2 className="font-serif-display text-3xl xs:text-4xl md:text-5xl text-[var(--c-text)] tracking-tight">
            Eleven Drafts, <span className="italic text-[var(--c-accent)]">Sixteen Years</span>
          </h2>
          <div className="hairline my-6 sm:my-8 max-w-xs" />
          <p className="text-[var(--c-text-muted)] text-sm sm:text-base md:text-lg leading-relaxed">
            Two shore-side training establishments, six surface ships of His
            Majesty's Fleet, the Submarine School at Dolphin, and a finale
            aboard a nuclear-armed Polaris submarine. Every ship you sailed
            in — gathered here for you to revisit.
          </p>
        </header>

        <div className="relative">
          {/* Center vertical line (desktop) */}
          <div
            className="hidden md:block absolute left-1/2 -translate-x-px top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[var(--c-accent)]/45 to-transparent"
            aria-hidden="true"
          />

          <ol className="space-y-10 sm:space-y-16 md:space-y-24">
            {SHIPS.map((ship, idx) => (
              <Entry key={ship.slug + idx} ship={ship} idx={idx} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Entry({ ship, idx }) {
  const ref = useReveal();
  const isRight = idx % 2 === 1;

  return (
    <li
      ref={ref}
      className="reveal relative md:grid md:grid-cols-2 md:gap-16 items-center"
      data-testid={`timeline-entry-${ship.slug}`}
    >
      {/* Connector dot */}
      <span
        className="hidden md:block absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[var(--c-accent)] ring-4 ring-[var(--c-bg)] z-10"
        aria-hidden="true"
      />

      {/* Image side */}
      <div className={`relative ${isRight ? "md:order-2" : "md:order-1"}`}>
        <Link
          to={`/ships/${ship.slug}`}
          className="block group relative overflow-hidden border border-[var(--c-border)]"
          data-testid={`timeline-image-link-${ship.slug}`}
        >
          <div className="aspect-[4/3] w-full overflow-hidden bg-[var(--c-bg-elev)]">
            <img
              src={ship.images[0].src}
              alt={ship.name}
              loading="lazy"
              className="archival-img w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-bg)]/85 via-[var(--c-bg)]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6 flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="overline truncate">{ship.dates}</p>
              <p className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[var(--c-text)] mt-1 truncate">
                {ship.name}
                {ship.pennant && ship.pennant !== "Shore Establishment" && (
                  <span className="text-[var(--c-accent)] italic ml-2 text-base sm:text-lg">
                    {ship.pennant}
                  </span>
                )}
              </p>
            </div>
            {ship.isSubmarine && (
              <span
                className="hidden md:inline-flex items-center gap-1 text-[var(--c-accent)] text-xs font-mono-plex uppercase tracking-widest border border-[var(--c-accent)]/40 px-2 py-1 shrink-0"
                title="Submarine Service"
              >
                <Waves size={12} /> Submarines
              </span>
            )}
          </div>
        </Link>
      </div>

      {/* Text side */}
      <div
        className={`mt-5 md:mt-0 ${isRight ? "md:order-1 md:text-right md:pr-8" : "md:order-2 md:pl-8"}`}
      >
        <div
          className={`flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 ${isRight ? "md:justify-end" : ""}`}
        >
          <span className="font-mono-plex text-[10px] sm:text-xs text-[var(--c-text-muted)]">
            {String(idx + 1).padStart(2, "0")} / 11
          </span>
          <span className="w-6 sm:w-8 h-px bg-[var(--c-accent)]/50" />
          <span className="overline truncate">{ship.type.split("(")[0].trim()}</span>
        </div>

        <h3 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[var(--c-text)] tracking-tight">
          {ship.name}
          {ship.pennant && ship.pennant !== "Shore Establishment" && (
            <span className="text-[var(--c-accent)] text-lg sm:text-xl md:text-2xl italic ml-2 sm:ml-3">
              {ship.pennant}
            </span>
          )}
        </h3>

        <p className="overline mt-2 sm:mt-3">{ship.dates}</p>

        <p
          className={`mt-4 sm:mt-5 text-[var(--c-text-muted)] text-sm sm:text-base md:text-lg leading-relaxed max-w-xl ${isRight ? "md:ml-auto" : ""}`}
        >
          {ship.summary}
        </p>

        <div className={`mt-5 sm:mt-6 flex ${isRight ? "md:justify-end" : ""}`}>
          <Link
            to={`/ships/${ship.slug}`}
            className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-[0.2em] text-[var(--c-accent)] hover:text-[var(--c-accent-bright)] active:text-[var(--c-accent-bright)] transition group min-h-[44px] py-3"
            data-testid={`timeline-readmore-${ship.slug}`}
          >
            Read the full history
            <ArrowUpRight
              size={14}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </Link>
        </div>
      </div>
    </li>
  );
}
